import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, ArrowLeft, Check, LayoutTemplate, Palette, ListChecks, Rocket, MessageSquare, Star, ThumbsUp, HeartHandshake, Trash2, Plus, Sparkles } from "lucide-react";

/* ---------- Survey type presets ---------- */
const TYPES = [
  {
    key: "nps",
    title: "NPS",
    tag: "Loyalty",
    icon: <Star size={22}/>,
    blurb: "The classic 0-10 recommendation score. Best for tracking loyalty over time.",
    color: "orange",
    starter: () => ([
      { id: crypto.randomUUID(), type: "nps", title: "How likely are you to recommend us to a friend or colleague?", required: true, min: 0, max: 10 },
      { id: crypto.randomUUID(), type: "long_text", title: "What's the primary reason for your score?", required: false, placeholder: "Tell us more…" },
    ]),
  },
  {
    key: "csat",
    title: "CSAT",
    tag: "Satisfaction",
    icon: <ThumbsUp size={22}/>,
    blurb: "1-5 star satisfaction, ideal after a specific interaction.",
    color: "navy",
    starter: () => ([
      { id: crypto.randomUUID(), type: "rating", title: "How satisfied were you with this experience?", required: true, max: 5 },
      { id: crypto.randomUUID(), type: "long_text", title: "What could we do better?", required: false },
    ]),
  },
  {
    key: "ces",
    title: "CES",
    tag: "Effort",
    icon: <HeartHandshake size={22}/>,
    blurb: "Customer Effort Score — how easy was it to solve the customer's issue?",
    color: "orange-soft",
    starter: () => ([
      { id: crypto.randomUUID(), type: "scale", title: "How easy was it to get your issue resolved?", required: true, min: 1, max: 7 },
      { id: crypto.randomUUID(), type: "long_text", title: "Anything you'd like us to improve?", required: false },
    ]),
  },
  {
    key: "custom",
    title: "Custom",
    tag: "Blank",
    icon: <MessageSquare size={22}/>,
    blurb: "Start blank and build whatever you need. 25+ question types available.",
    color: "grey",
    starter: () => ([]),
  },
];

/* ---------- Theme presets ---------- */
const THEMES = [
  { key: "cx10x",  name: "CX10X",   primary: "#F58220", accent: "#1F3A6B", background: "#FFFFFF", text: "#1F3A6B",  button_text: "#FFFFFF" },
  { key: "navy",   name: "Navy",    primary: "#1F3A6B", accent: "#F58220", background: "#FFFFFF", text: "#1F3A6B",  button_text: "#FFFFFF" },
  { key: "forest", name: "Forest",  primary: "#0F766E", accent: "#F59E0B", background: "#F0FDFA", text: "#134E4A",  button_text: "#FFFFFF" },
  { key: "rose",   name: "Rose",    primary: "#E11D48", accent: "#1F3A6B", background: "#FFF1F2", text: "#881337",  button_text: "#FFFFFF" },
  { key: "ink",    name: "Ink",     primary: "#111827", accent: "#F58220", background: "#FFFFFF", text: "#111827",  button_text: "#FFFFFF" },
];

/* ---------- Step meta ---------- */
const STEPS = [
  { num: 1, label: "Type",     Icon: LayoutTemplate },
  { num: 2, label: "Theme",    Icon: Palette },
  { num: 3, label: "Questions", Icon: ListChecks },
  { num: 4, label: "Publish",  Icon: Rocket },
];

export default function SurveyWizardPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [type, setType] = useState(TYPES[0].key);
  const [theme, setTheme] = useState(THEMES[0].key);
  const [meta, setMeta] = useState({ title: "", description: "" });
  const [questions, setQuestions] = useState(TYPES[0].starter());

  // Reset the starter questions when the user picks a different type — but only if the questions
  // list matches the previous type's starter (i.e. user hasn't customized yet).
  const pickType = (k) => {
    const prev = TYPES.find((t) => t.key === type);
    const untouched = prev && JSON.stringify(prev.starter().map(q => q.type)) === JSON.stringify(questions.map(q => q.type));
    setType(k);
    if (untouched) {
      const next = TYPES.find((t) => t.key === k);
      setQuestions(next.starter());
    }
  };

  const canAdvance = () => {
    // Step 1 requires a survey title before moving on.
    if (step === 1 && !meta.title.trim()) return false;
    // Step 3 requires at least one question and no placeholder titles.
    if (step === 3) {
      if (questions.length === 0) return false;
      if (questions.some((q) => !q.title || !q.title.trim() || q.title.trim().toLowerCase() === "untitled question")) return false;
    }
    return true;
  };

  const publish = async () => {
    setBusy(true); setErr("");
    try {
      const th = THEMES.find((t) => t.key === theme);
      const { data } = await api.post("/surveys", {
        title: meta.title.trim(),
        description: meta.description.trim() || null,
        questions,
        theme: {
          primary: th.primary, accent: th.accent, background: th.background,
          text: th.text, button_text: th.button_text,
        },
      });
      // Publish immediately for the "Rocket" step promise. Surface a warning if it fails.
      try {
        await api.put(`/surveys/${data.id}`, { is_published: true, status: "published" });
      } catch (_) {
        setErr("Survey created as a draft — publish failed. You can publish from the builder.");
      }
      nav(`/app/surveys/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const goNext = () => {
    if (step >= STEPS.length) return publish();
    setStep((s) => Math.min(STEPS.length, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="max-w-3xl mx-auto w-full fade-in">
      {/* Progress rail */}
      <ol className="grid grid-cols-4 gap-2 md:gap-3 mb-6 md:mb-8">
        {STEPS.map((s) => {
          const done = step > s.num;
          const active = step === s.num;
          const Icon = s.Icon;
          return (
            <li key={s.num} className="flex flex-col items-center gap-1.5" data-testid={`wizard-step-${s.num}${active ? "-active" : ""}`}>
              <div
                className="w-10 h-10 rounded-2xl grid place-items-center transition-all"
                style={{
                  background: done ? "var(--brand)" : active ? "linear-gradient(135deg, var(--brand) 0%, var(--brand-strong) 100%)" : "var(--ink-100)",
                  color: done || active ? "#FFFFFF" : "var(--ink-400)",
                  boxShadow: active ? "0 12px 20px -10px rgba(245,130,32,0.55)" : "none",
                  transform: active ? "scale(1.05)" : "scale(1)",
                }}
              >
                {done ? <Check size={18}/> : <Icon size={18}/>}
              </div>
              <span className="text-[11px] font-bold" style={{ color: active ? "var(--brand-strong)" : done ? "var(--ink-700)" : "var(--ink-400)" }}>{s.label}</span>
            </li>
          );
        })}
      </ol>

      {/* Panel */}
      <Card className="p-5 md:p-8 rounded-3xl border-0 bg-white" style={{ boxShadow: "var(--shadow-soft)" }}>
        {step === 1 && (
          <StepType type={type} onPick={pickType} meta={meta} setMeta={setMeta}/>
        )}
        {step === 2 && (
          <StepTheme theme={theme} onPick={setTheme} />
        )}
        {step === 3 && (
          <StepQuestions questions={questions} setQuestions={setQuestions} meta={meta} setMeta={setMeta}/>
        )}
        {step === 4 && (
          <StepPublish meta={meta} type={type} theme={theme} questions={questions}/>
        )}

        {err && <p className="mt-4 text-sm px-3 py-2 rounded-xl" style={{ color: "var(--detractor)", backgroundColor: "var(--detractor-bg)" }}>{err}</p>}

        <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between gap-3">
          {step > 1 ? (
            <button data-testid="wizard-back" onClick={goBack} className="btn-pill ghost text-sm">
              <ArrowLeft size={16} className="mr-1"/> Back
            </button>
          ) : (
            <Link to="/app/surveys">
              <button className="btn-pill ghost text-sm" data-testid="wizard-cancel">Cancel</button>
            </Link>
          )}
          <div className="text-[11px] font-semibold" style={{ color: "var(--ink-400)" }}>
            Step {step} of {STEPS.length}
          </div>
          <button
            data-testid="wizard-next"
            onClick={goNext}
            disabled={!canAdvance() || busy}
            className="btn-pill text-sm disabled:opacity-50"
          >
            {step === STEPS.length ? (busy ? "Publishing…" : <>Publish <Rocket size={14} className="ml-1"/></>) : <>Next <ArrowRight size={14} className="ml-1"/></>}
          </button>
        </div>
      </Card>
    </div>
  );
}

/* ---------- Step 1: Type + title ---------- */
function StepType({ type, onPick, meta, setMeta }) {
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--brand-strong)" }}>Step 1</p>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight mt-1" style={{ color: "var(--ink-900)" }}>What kind of survey?</h2>
      <p className="text-sm mt-1" style={{ color: "var(--ink-500)" }}>Pick a starter — you can always add or remove questions later.</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        {TYPES.map((t) => {
          const active = type === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onPick(t.key)}
              data-testid={`wizard-type-${t.key}`}
              className="text-left p-4 rounded-2xl transition-all bg-white"
              style={{
                border: active ? "2px solid var(--brand)" : "1px solid var(--border)",
                boxShadow: active ? "var(--shadow-pop)" : "var(--shadow-soft)",
                transform: active ? "translateY(-1px)" : "none",
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: active ? "var(--brand)" : "var(--brand-soft)", color: active ? "#fff" : "var(--brand-strong)" }}>
                  {t.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-black text-[15px]" style={{ color: "var(--ink-900)" }}>{t.title}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: "var(--secondary-soft)", color: "var(--secondary)" }}>{t.tag}</span>
                  </div>
                  <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: "var(--ink-500)" }}>{t.blurb}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-7 grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--ink-500)" }}>Survey title *</Label>
          <Input
            data-testid="wizard-title"
            value={meta.title}
            onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))}
            placeholder="e.g. Post-purchase NPS — Q1"
            className="mt-1.5 h-11 rounded-xl"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--ink-500)" }}>Description (optional)</Label>
          <Input
            data-testid="wizard-desc"
            value={meta.description}
            onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
            placeholder="A one-line internal description"
            className="mt-1.5 h-11 rounded-xl"
          />
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 2: Theme ---------- */
function StepTheme({ theme, onPick }) {
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--brand-strong)" }}>Step 2</p>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight mt-1" style={{ color: "var(--ink-900)" }}>Pick a theme</h2>
      <p className="text-sm mt-1" style={{ color: "var(--ink-500)" }}>Your survey inherits these colors. You can fine-tune everything later in the builder.</p>

      <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {THEMES.map((t) => {
          const active = theme === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onPick(t.key)}
              data-testid={`wizard-theme-${t.key}`}
              className="text-left rounded-2xl overflow-hidden transition-all bg-white group"
              style={{
                border: active ? "2px solid var(--brand)" : "1px solid var(--border)",
                boxShadow: active ? "var(--shadow-pop)" : "var(--shadow-soft)",
              }}
            >
              <div className="h-24 relative" style={{ backgroundColor: t.background }}>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="w-14 h-14 rounded-full grid place-items-center text-white font-black text-lg" style={{ backgroundColor: t.primary, boxShadow: `0 12px 22px -10px ${t.primary}80` }}>Aa</div>
                </div>
                <div className="absolute bottom-2 right-2 flex gap-1">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.primary }}/>
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.accent }}/>
                  <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: t.background, borderColor: "var(--border-strong)" }}/>
                </div>
              </div>
              <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
                <p className="font-black text-[13px]" style={{ color: "var(--ink-900)" }}>{t.name}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--ink-400)" }}>{t.primary}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Step 3: Questions (lightweight) ---------- */
const Q_KINDS = [
  { key: "nps",       label: "NPS 0-10" },
  { key: "rating",    label: "Star rating" },
  { key: "scale",     label: "1-7 scale" },
  { key: "short_text", label: "Short text" },
  { key: "long_text",  label: "Long text" },
  { key: "single_select", label: "Single choice" },
  { key: "multi_select",  label: "Multiple choice" },
  { key: "yes_no",    label: "Yes / No" },
];

function StepQuestions({ questions, setQuestions, meta, setMeta }) {
  const addQ = (kind) => {
    const base = { id: crypto.randomUUID(), type: kind, title: "Untitled question", required: false };
    if (kind === "nps") Object.assign(base, { min: 0, max: 10 });
    if (kind === "rating") Object.assign(base, { max: 5 });
    if (kind === "scale") Object.assign(base, { min: 1, max: 7 });
    if (kind === "single_select" || kind === "multi_select") Object.assign(base, { options: ["Option 1", "Option 2", "Option 3"] });
    setQuestions((qs) => [...qs, base]);
  };
  const update = (i, patch) => setQuestions((qs) => qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const remove = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--brand-strong)" }}>Step 3</p>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight mt-1" style={{ color: "var(--ink-900)" }}>Add your questions</h2>
      <p className="text-sm mt-1" style={{ color: "var(--ink-500)" }}>Keep it short — 3-5 questions get the highest completion rate. You can add more later.</p>

      {!meta.title.trim() && (
        <div className="mt-4 flex items-center gap-2 text-[12.5px] px-3 py-2 rounded-xl" style={{ backgroundColor: "var(--brand-soft)", color: "var(--brand-strong)" }}>
          <Sparkles size={14}/>
          <span>Give your survey a title in Step 1 before publishing.</span>
        </div>
      )}
      {questions.some((q) => (q.title || "").trim().toLowerCase() === "untitled question") && (
        <div className="mt-4 flex items-center gap-2 text-[12.5px] px-3 py-2 rounded-xl" style={{ backgroundColor: "var(--detractor-bg)", color: "var(--detractor)" }}>
          <span className="font-black">!</span>
          <span>Rename every &quot;Untitled question&quot; before you can move on.</span>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {questions.length === 0 && (
          <div className="p-6 text-center rounded-2xl border-2 border-dashed" style={{ borderColor: "var(--border)" }}>
            <p className="text-sm" style={{ color: "var(--ink-500)" }}>No questions yet. Pick a type below to add one.</p>
          </div>
        )}
        {questions.map((q, i) => (
          <div key={q.id} data-testid={`wizard-q-${i}`} className="p-4 rounded-2xl bg-white flex items-start gap-3" style={{ boxShadow: "var(--shadow-soft)", border: "1px solid var(--border)" }}>
            <div className="w-8 h-8 rounded-xl grid place-items-center font-black shrink-0" style={{ backgroundColor: "var(--brand-soft)", color: "var(--brand-strong)" }}>Q{i + 1}</div>
            <div className="flex-1 min-w-0">
              <Input
                data-testid={`wizard-q-title-${i}`}
                value={q.title}
                onChange={(e) => update(i, { title: e.target.value })}
                placeholder="Question text…"
                className="rounded-xl h-11 font-semibold"
              />
              <div className="mt-2 flex items-center gap-2 text-[11px]">
                <span className="px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: "var(--secondary-soft)", color: "var(--secondary)" }}>{Q_KINDS.find(k => k.key === q.type)?.label || q.type}</span>
                <label className="inline-flex items-center gap-1.5 font-semibold cursor-pointer" style={{ color: "var(--ink-500)" }}>
                  <input type="checkbox" checked={!!q.required} onChange={(e) => update(i, { required: e.target.checked })} data-testid={`wizard-q-req-${i}`} className="accent-[#F58220]"/>
                  Required
                </label>
              </div>
              {(q.type === "single_select" || q.type === "multi_select") && (
                <Textarea
                  data-testid={`wizard-q-options-${i}`}
                  value={(q.options || []).join("\n")}
                  onChange={(e) => update(i, { options: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                  rows={3}
                  placeholder={"Option 1\nOption 2\nOption 3"}
                  className="mt-2 rounded-xl text-sm"
                />
              )}
            </div>
            <button data-testid={`wizard-q-remove-${i}`} onClick={() => remove(i)} className="w-9 h-9 rounded-xl grid place-items-center shrink-0 transition-colors hover:bg-rose-50 text-rose-500" aria-label="Remove question">
              <Trash2 size={16}/>
            </button>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <p className="text-[11px] uppercase tracking-wider font-bold mb-2" style={{ color: "var(--ink-500)" }}>Add a question</p>
        <div className="flex flex-wrap gap-2">
          {Q_KINDS.map((k) => (
            <button key={k.key} onClick={() => addQ(k.key)} data-testid={`wizard-add-${k.key}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold bg-white transition-colors hover:bg-[#FFF0DF]" style={{ border: "1px solid var(--border-strong)", color: "var(--ink-700)" }}>
              <Plus size={12}/> {k.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 4: Publish ---------- */
function StepPublish({ meta, type, theme, questions }) {
  const t = TYPES.find((x) => x.key === type);
  const th = THEMES.find((x) => x.key === theme);
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--brand-strong)" }}>Step 4</p>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight mt-1" style={{ color: "var(--ink-900)" }}>Ready to launch! 🚀</h2>
      <p className="text-sm mt-1" style={{ color: "var(--ink-500)" }}>Review your survey below. Hit Publish and we&apos;ll take you to the builder for advanced options (logic, sections, distribution).</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <SummaryRow label="Title" value={meta.title || "(untitled)"} />
        <SummaryRow label="Type" value={t?.title} />
        <SummaryRow label="Theme" value={th?.name} swatch={th?.primary} />
        <SummaryRow label="Questions" value={String(questions.length)} />
      </div>

      <div className="mt-6 rounded-2xl overflow-hidden" style={{ boxShadow: "var(--shadow-soft)", border: "1px solid var(--border)" }}>
        <div className="px-4 py-3 flex items-center gap-2 text-white" style={{ background: `linear-gradient(120deg, ${th?.primary}, ${th?.accent})` }}>
          <div className="w-8 h-8 rounded-lg bg-white/20 grid place-items-center">
            <MessageSquare size={16}/>
          </div>
          <p className="font-bold text-[14px] truncate">{meta.title || "(untitled survey)"}</p>
        </div>
        <ul className="divide-y divide-slate-100 bg-white">
          {questions.slice(0, 5).map((q, i) => (
            <li key={q.id} className="px-4 py-3 flex items-start gap-3 text-[13px]">
              <span className="w-6 h-6 rounded-md grid place-items-center text-[11px] font-black shrink-0" style={{ backgroundColor: "var(--brand-soft)", color: "var(--brand-strong)" }}>{i + 1}</span>
              <span className="flex-1" style={{ color: "var(--ink-700)" }}>{q.title}</span>
              {q.required && <span className="text-[10px] font-bold" style={{ color: "var(--detractor)" }}>required</span>}
            </li>
          ))}
          {questions.length > 5 && <li className="px-4 py-2 text-[11px]" style={{ color: "var(--ink-400)" }}>+ {questions.length - 5} more…</li>}
          {questions.length === 0 && <li className="px-4 py-5 text-center text-[13px]" style={{ color: "var(--ink-400)" }}>No questions yet — add some in step 3.</li>}
        </ul>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, swatch }) {
  return (
    <div className="p-3 rounded-2xl" style={{ backgroundColor: "var(--ink-25)", border: "1px solid var(--border)" }}>
      <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--ink-400)" }}>{label}</p>
      <div className="mt-1 flex items-center gap-2">
        {swatch && <span className="w-4 h-4 rounded-full" style={{ backgroundColor: swatch }}/>}
        <p className="font-bold text-[14px]" style={{ color: "var(--ink-900)" }}>{value}</p>
      </div>
    </div>
  );
}
